/* eslint-disable @typescript-eslint/no-invalid-void-type */
import { Options } from '../options.interface';
import { HttpClient, HttpEvent, HttpEventType, HttpHeaders, HttpRequest, HttpResponse } from '@angular/common/http';
import { Injectable, InjectionToken, inject } from '@angular/core';
import { readBlob } from '@wizbii-utils/angular/core';
import { WizFile } from '@wizbii/utils/models';
import { Observable, of, zip } from 'rxjs';
import { map, mergeMap, switchMap } from 'rxjs/operators';

interface UploadUrlResponse {
  uploadUrl: string;
  successUrl: string;
  fileId: string;
}

export interface AdditionalPermissions {
  additionalAllowedRoles?: string[];
  additionalAllowedUsers?: string[];
  additionalAllowedApps?: string[];
}

export interface FileApiConfig {
  baseUrl: string;
  googleStorageUrl: string;
  wizbiiFilesBucket: string;
}

export const FILE_API_CONFIG = new InjectionToken<FileApiConfig>('FILE_API_CONFIG');

@Injectable()
export class FileWebservice {
  readonly #http = inject(HttpClient);
  readonly #fileApiConfig = inject(FILE_API_CONFIG);

  readonly #baseUrl = this.#fileApiConfig.baseUrl;
  readonly #googleStorageUrl = this.#fileApiConfig.googleStorageUrl;
  readonly #wizbiiFilesBucket = this.#fileApiConfig.wizbiiFilesBucket;

  /**
   * Create a file and send it to GCS and needed API
   * @param {Object} file File to create
   * @param {Object} isPublic Boolean for the file to be private
   * @param {Object} additionalPermissions Object to set file's additional permissions
   * @param {Object} [options] Angular Options for the request
   */
  create(
    file: WizFile,
    isPublic = true,
    additionalPermissions?: AdditionalPermissions,
    options?: Options
  ): Observable<HttpEvent<any>> {
    const res = /\.(\w+)$/.exec(file.domFile.name);

    const uploadUrlResponse$: Observable<UploadUrlResponse> = this.#http.post<UploadUrlResponse>(
      `${this.#baseUrl}/v1/file`,
      {
        fileExtension: res && res.length > 0 ? res[1] : '',
        contentType: file.domFile.type,
        public: isPublic,
        ...additionalPermissions,
      },
      options
    );

    const buffer$ = readBlob(file.domFile);

    return zip(buffer$, uploadUrlResponse$).pipe(
      switchMap(([buffer, uploadUrlResponse]) => {
        const req = new HttpRequest('PUT', uploadUrlResponse.uploadUrl, buffer, {
          headers: new HttpHeaders({ ...(options ? options.headers : {}), 'Content-Type': file.domFile.type }),
          reportProgress: true,
        });
        return this.#http.request(req).pipe(
          mergeMap(
            (event: HttpEvent<any>): Observable<HttpEvent<any>> =>
              event.type === HttpEventType.Response
                ? this.#http.post<void>(uploadUrlResponse.successUrl, null, options).pipe(
                    map(
                      () =>
                        new HttpResponse<WizFile>({
                          body: new WizFile({ ...file, id: uploadUrlResponse.fileId }),
                        })
                    )
                  )
                : of(event)
          )
        );
      })
    );
  }

  /**
   * Create a blob from a file id
   * @param {string} fileId Id of the file
   * @param {Object} [options] Angular Options for the request
   */
  getFileUrl(fileId: string, options?: Options): Observable<string> {
    return this.#http.get<{ url: string }>(`${this.#baseUrl}/v1/file/${fileId}`, options).pipe(map((val) => val.url));
  }

  /**
   * Create a blob from a file id
   * @param {string} fileId Id of the file
   * @param {Object} [options] Angular Options for the request
   */
  getDownloadBlob(fileId: string, options?: Options): Observable<Blob> {
    return this.getFileUrl(fileId, options).pipe(
      switchMap((url) => this.#http.get(url, { ...options, responseType: 'blob' }))
    );
  }

  /**
   * Build public URL from a file id
   * @param {string} fileId Id of the file
   * @deprecated
   */
  __getFileUrl(fileId: string): string {
    return `${this.#googleStorageUrl}/${this.#wizbiiFilesBucket}/${fileId}`;
  }

  /**
   * Delete a file with the id
   * @param {string} fileId Id of the file
   * @param {Object} [options] Angular Options for the request
   */
  delete(fileId: string, options?: Options): Observable<void> {
    const url = `${this.#baseUrl}/v1/file/${fileId}`;
    return this.#http.delete<void>(url, options);
  }
}
